/**
 * v1 `networkid/{UAS_ID}` adapter.
 *
 * Maps the legacy AirMarket networkid payload (sample in v1's
 * `src/data/drone.js`) onto the v2 `TelemetryEnvelope`. Fields v1 doesn't
 * carry (battery, flight_mode, ops_status, status, etc.) are left null so
 * the UI shows `—` placeholders until a richer envelope ships.
 *
 * Reference: AirMarket DJI Cloud / C2 saving pipeline publishes the
 * `networkid/<UAS_ID>` topic at roughly 1 Hz with the canonical Remote-ID
 * fields plus a few AirMarket extensions (`callsign`, `source_component`,
 * `track_origin_type`).
 */
import { z } from 'zod';
import {
  type TelemetryEnvelope,
  TelemetryEnvelope as TelemetryEnvelopeSchema,
} from '@/lib/mqtt/schemas/telemetry';
import { resolveFleetIdentity } from './fleetIdentity';

/** Subset of the v1 payload we rely on. Extra fields are tolerated; missing
 *  fields use sensible defaults so a partial message still maps cleanly.
 *
 *  Two payload shapes coexist on the wire today (sniffed 2026-05-15):
 *    "rich"  — aircraft-agent publishers (SIMTL70/ORIN01) carry battery,
 *              alt_agl, alt_asl, armed, model, timestamp_utc, etc.
 *    "lean"  — simulator publishers (SIMTL69/SIMTL71) carry the core
 *              Remote-ID fields plus track_origin_type only.
 *  All "lean"-shape fields are present in the "rich" shape too. */
export const V1NetworkidPayload = z.object({
  // Core (both shapes)
  UAS_ID: z.string(),
  callsign: z.string().nullish(),
  UAS_TYPE: z.number().int().nullish(),
  source_component: z.string().nullish(),
  source: z.string().nullish(),
  track_origin_type: z.string().nullish(),
  timestamp: z.string().nullish(),
  lat: z.number(),
  lon: z.number(),
  heading: z.number().nullish(),
  geodetic_altitude: z.number().nullish(),
  pressure_altitude: z.number().nullish(),
  horizontal_accuracy: z.number().nullish(),
  vertical_accuracy: z.number().nullish(),
  speed_accuracy: z.number().nullish(),
  horizontal_speed: z.number().nullish(),
  vertical_speed: z.number().nullish(),
  // Rich shape (aircraft-agent publishers)
  timestamp_utc: z.string().nullish(),
  battery: z.number().nullish(),      // -1 = sensor unavailable
  alt_agl: z.number().nullish(),      // metres above ground
  alt_asl: z.number().nullish(),      // metres above sea level
  // DJI Flight Hub 2 publishers (source: "dji") emit armed as 0/1 rather than
  // a JSON boolean; accept both so the whole message isn't rejected over one field.
  armed: z
    .union([z.boolean(), z.number()])
    .transform((v) => (typeof v === 'number' ? v !== 0 : v))
    .nullish(),
  model: z.string().nullish(),
  drone_type: z.string().nullish(),   // string (e.g. "Quadcopter"); falls back to UAS_TYPE map
  // Mission metadata — published only by aircraft_agent rich payloads.
  // Verified by sniff 2026-05-26 against staging: SIMTL70 networkid
  // carries `full_name`, `mission_name`, `mission_main_flight_type`,
  // `legal_profile`, `missionlocation`, `mission_notes`, `flight_guid`.
  // Used to backfill the OI envelope's pilot / op_type / legal_rule etc.
  // when the flightplan topic itself doesn't ship them (it doesn't
  // today). All nullish — lean publishers don't carry these.
  full_name: z.string().nullish(),
  pilot_id: z.union([z.string(), z.number()]).nullish(),
  mission_name: z.string().nullish(),
  mission_main_flight_type: z.string().nullish(),
  mission_notes: z.string().nullish(),
  missionlocation: z.string().nullish(),
  legal_profile: z.string().nullish(),
  flight_guid: z.string().nullish(),
  drone_guid: z.string().nullish(),
});
export type V1NetworkidPayload = z.infer<typeof V1NetworkidPayload>;

const M_PER_DEG_LAT = 111_320;
const M_TO_FT = 3.28084;
const mPerDegLng = (lat: number) => 111_320 * Math.cos((lat * Math.PI) / 180);

/** v1's `source` enum doesn't map 1:1 to v2's apType; bucket what we know
 *  and leave the rest as `'custom'`. */
function mapApType(source: string | null | undefined): 'px4' | 'ardupilot' | 'dji' | 'custom' {
  if (!source) return 'custom';
  const s = source.toLowerCase();
  if (s.includes('dji') || s.includes('fh2')) return 'dji';
  if (s.includes('ardu') || s.includes('px4')) return s.includes('px4') ? 'px4' : 'ardupilot';
  return 'custom';
}

/** GPS-lock heuristic — v1 doesn't expose a discrete lock state, but
 *  horizontal_accuracy is a good proxy: <15 m = Acquired, <50 m =
 *  Acquiring, anything worse (or missing) is Lost. */
function gpsLockFromAccuracy(ha: number | null | undefined): 'Acquired' | 'Acquiring' | 'Lost' {
  if (ha == null) return 'Lost';
  if (ha < 15) return 'Acquired';
  if (ha < 50) return 'Acquiring';
  return 'Lost';
}

/** Normalise a v1 timestamp string to UTC "Z" form. v1 publishes both
 *  "2026-05-15T09:16:53.747Z" (aircraft_agent) and "...386158+00:00"
 *  (simulator); Date round-trip via toISOString collapses both to "Z". */
function normaliseIsoUtc(s: string | null | undefined): string {
  if (!s) return new Date().toISOString();
  const t = Date.parse(s);
  if (Number.isNaN(t)) return new Date().toISOString();
  return new Date(t).toISOString();
}

/** v1's `battery` field uses -1 to mean "no battery sensor data". */
function batteryPct(b: number | null | undefined): number | null {
  if (b == null) return null;
  if (b < 0) return null; // sentinel for "no data"
  if (b > 100) return 100;
  return b;
}

/** Convert a v1 networkid payload into a v2 TelemetryEnvelope. Returns
 *  null if the input is malformed (missing UAS_ID / coordinates). */
export function networkidToTelemetry(raw: unknown): TelemetryEnvelope | null {
  const parsed = V1NetworkidPayload.safeParse(raw);
  if (!parsed.success) return null;
  const v1 = parsed.data;

  const lat = v1.lat;
  const lng = v1.lon;
  const headingKnown = v1.heading != null && v1.heading !== 361;
  const heading = (((headingKnown ? v1.heading : 0) ?? 0) + 360) % 360;
  const speedMps = v1.horizontal_speed ?? 0;

  // T+60 s linear extrapolation from current heading + ground speed.
  // `pred1min.inside` starts null here — this adapter has the position but
  // not the OI polygon (it lands on flightplan/<UAS_ID>). The drones store
  // fills `inside` in via deriveOiContainment() once both halves are present.
  const headingRad = (heading * Math.PI) / 180;
  const aheadM = speedMps * 60;
  const pred1min =
    speedMps > 0
      ? {
          lat: lat + (aheadM * Math.cos(headingRad)) / M_PER_DEG_LAT,
          lng: lng + (aheadM * Math.sin(headingRad)) / mPerDegLng(lat),
          inside: null,
        }
      : { lat, lng, inside: null };

  const identity = resolveFleetIdentity(v1.UAS_ID, v1.UAS_TYPE);
  const apType = mapApType(v1.source);

  // Prefer the rich-shape fields when present, fall back to the lean shape.
  // `alt_asl` (m) takes precedence over `geodetic_altitude` because v1's
  // aircraft-agent publishes both with identical values; the lean shape
  // only carries `geodetic_altitude`.
  const aslM = v1.alt_asl ?? v1.geodetic_altitude ?? null;
  const aglM = v1.alt_agl ?? null;
  const aglFt = aglM != null ? aglM * M_TO_FT : null;

  // Backend-supplied `model` / `drone_type` (string) beat the static
  // identity lookup when populated — useful for drones we haven't
  // pre-registered.
  const droneModel = v1.model?.trim() || identity.drone_model;
  const droneType = v1.drone_type?.trim() || identity.drone_type;

  // Build then validate through the v2 schema so any drift in field
  // shapes surfaces immediately at the worker boundary.
  // Note: we deliberately leave `flight_mode` null here. v1 doesn't publish
  // a real autopilot mode on networkid; MAVLink HEARTBEAT carries the real
  // ArduPilot mode (AUTO/RTL/LOITER/etc) and is authoritative. Earlier
  // versions of this mapper set it to "ARMED"/"DISARMED" from raw.armed,
  // but that fought MAVLink's value at 1 Hz and caused the UI badge to
  // flicker. Better to leave null for MAVLink-less drones than to clobber.
  const built: TelemetryEnvelope = {
    drone_id: v1.UAS_ID,
    callsign: v1.callsign?.trim() || identity.callsign || v1.UAS_ID,
    drone_model: droneModel,
    drone_type: droneType,
    lat,
    lng,
    heading,
    heading_known: headingKnown,
    asl: aslM,
    agl: aglFt,
    aglM,
    atl: aglFt,    // mirror AGL until take-off-snapshot is wired
    atlM: aglM,
    battery_pct: batteryPct(v1.battery),
    battery_v: null,
    flight_mode: null,
    // Written only when the publisher sends it (DJI as 0/1, MAVLink as a boolean —
    // both already normalised to boolean by the payload schema above).
    ...(v1.armed != null ? { armed: v1.armed } : {}),
    flight_time: null,
    gps_sats: null,
    // Loose HDOP proxy: divide accuracy (m) by ~5 to get a 0..3 scale.
    // Not authoritative — but better than null for the Overview tile.
    gps_hdop: v1.horizontal_accuracy != null ? v1.horizontal_accuracy / 5 : null,
    gps_lock: gpsLockFromAccuracy(v1.horizontal_accuracy),
    groundspeed: speedMps,
    climbRate: v1.vertical_speed ?? null,
    acceleration: null,
    signal: null,
    ops_status: null,
    status: null,
    status_label: null,
    confidence: null,
    proximity: null,
    conflict: null,
    pred1min,
    nmacRadius: identity.nmacRadius,
    swcRadius: identity.swcRadius,
    evalRadius: identity.evalRadius,
    _meta: { apType },
    // Prefer `timestamp_utc` (always Z-suffixed); normalise either form
    // through Date round-trip so Zod's strict datetime accepts both.
    received_at: normaliseIsoUtc(v1.timestamp_utc ?? v1.timestamp),
  };

  // Schema validation guarantees downstream consumers see a clean shape
  // even if v1 ever ships a malformed value (NaN coords, etc.).
  const validated = TelemetryEnvelopeSchema.safeParse(built);
  return validated.success ? validated.data : null;
}

/** Extract OI-relevant mission metadata from a (rich) networkid payload.
 *
 *  Verified live source (sniff 2026-05-26): aircraft_agent publishers
 *  ship pilot identity (`full_name`), mission descriptor
 *  (`mission_name`, `mission_main_flight_type`, `mission_notes`,
 *  `missionlocation`), legal profile (`legal_profile`), and an extra
 *  flight identifier (`flight_guid`) inside each networkid message.
 *  The flightplan topic itself doesn't ship most of these, so this is
 *  the practical source for OI panel's Pilot / Operation Type /
 *  Legal Rule rows in live mode.
 *
 *  Returns null if no rich fields are present (lean publishers).
 *  Caller is expected to merge the partial into the per-drone OI
 *  snapshot in `MqttSourceLegacy.lastOi`. */
export interface NetworkidOiUpdate {
  drone_id: string;
  partial: {
    pilot_name?: string | null;
    op_type?: string | null;
    legal_rule?: string | null;
    oi_id?: string;
    oi_ref_id?: string | null;
    mission_name?: string | null;
    mission_notes?: string | null;
    mission_location?: string | null;
  };
}

export function networkidToOiUpdate(raw: unknown): NetworkidOiUpdate | null {
  const parsed = V1NetworkidPayload.safeParse(raw);
  if (!parsed.success) return null;
  const v1 = parsed.data;

  const fullName = v1.full_name?.trim() || null;
  const missionType = v1.mission_main_flight_type?.trim() || null;
  const legalProfile = v1.legal_profile?.trim() || null;
  const missionName = v1.mission_name?.trim() || null;
  const missionNotes = v1.mission_notes?.trim() || null;
  const missionLocation = v1.missionlocation?.trim() || null;
  const flightGuid = v1.flight_guid?.trim() || null;

  // No rich data → caller has nothing to merge.
  if (
    !fullName &&
    !missionType &&
    !legalProfile &&
    !missionName &&
    !missionNotes &&
    !missionLocation &&
    !flightGuid
  ) {
    return null;
  }

  const partial: NetworkidOiUpdate['partial'] = {};
  if (fullName) partial.pilot_name = fullName;
  if (missionType) partial.op_type = missionType;
  if (legalProfile) partial.legal_rule = legalProfile;
  if (missionName) partial.mission_name = missionName;
  if (missionNotes) partial.mission_notes = missionNotes;
  if (missionLocation) partial.mission_location = missionLocation;
  if (flightGuid) {
    partial.oi_id = flightGuid;
    partial.oi_ref_id = flightGuid;
  }

  return { drone_id: v1.UAS_ID, partial };
}
